import type { ChatSession, StoredSession, StorageManager } from '../types';

class LocalStorageManager implements StorageManager {
  private readonly storageKey = 'oneai-chatbot-sessions';
  private readonly maxSessions = 50; // Limit to prevent storage overflow

  getSessions(): StoredSession[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];
      
      const sessions: StoredSession[] = JSON.parse(stored);
      // Sort by updatedAt desc (most recent first)
      return sessions.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } catch {
      // Failed to load sessions, return empty array
      return [];
    }
  }

  getSession(id: string): StoredSession | null {
    const sessions = this.getSessions();
    return sessions.find(session => session.id === id) || null;
  }

  saveSession(session: ChatSession): void {
    try {
      const sessions = this.getSessions();
      const existingIndex = sessions.findIndex(s => s.id === session.id);
      
      const storedSession: StoredSession = {
        id: session.id,
        title: session.title,
        messages: session.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp.toISOString(),
        })),
        createdAt: session.createdAt.toISOString(),
        updatedAt: session.updatedAt.toISOString(),
      };

      if (existingIndex >= 0) {
        sessions[existingIndex] = storedSession;
      } else {
        sessions.unshift(storedSession);
        
        // Limit number of sessions
        if (sessions.length > this.maxSessions) {
          sessions.splice(this.maxSessions);
        }
      }

      localStorage.setItem(this.storageKey, JSON.stringify(sessions));
    } catch {
      // Failed to save session
    }
  }

  deleteSession(id: string): void {
    try {
      const sessions = this.getSessions();
      const filteredSessions = sessions.filter(session => session.id !== id);
      localStorage.setItem(this.storageKey, JSON.stringify(filteredSessions));
    } catch {
      // Failed to delete session
    }
  }

  clearAllSessions(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Failed to clear sessions
    }
  }

  // Convert stored session back to ChatSession
  hydrateChatSession(stored: StoredSession): ChatSession {
    return {
      id: stored.id,
      title: stored.title,
      messages: stored.messages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp),
      })),
      createdAt: new Date(stored.createdAt),
      updatedAt: new Date(stored.updatedAt),
    };
  }

  // Generate a session title from the first user message
  generateSessionTitle(firstMessage: string): string {
    const maxLength = 50;
    if (firstMessage.length <= maxLength) {
      return firstMessage;
    }
    return firstMessage.substring(0, maxLength - 3) + '...';
  }
}

export default LocalStorageManager;
